import type {Table as ReactTable} from '@tanstack/react-table';
import {ChevronLeft, ChevronRight} from 'lucide-react';
import {useTranslation} from 'react-i18next';

import {IconButton} from '@/components/ui/icon-button';

interface TablePaginationProps<TData> {
  table: ReactTable<TData>;
  maxPagesToShow?: number;
}

function TablePagination<TData>({
  table,
  maxPagesToShow = 3,
}: TablePaginationProps<TData>) {
  const {t} = useTranslation();
  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  const startPageIndex = Math.max(
    Math.min(
      pageIndex - Math.floor(maxPagesToShow / 2),
      pageCount - maxPagesToShow
    ),
    0
  );

  const endPageIndex = Math.min(startPageIndex + maxPagesToShow, pageCount);

  return (
    <div className="flex flex-row items-center gap-3">
      <div className="flex grow flex-row items-center gap-1">
        <IconButton
          variant="secondary"
          size="small"
          onClick={() => {
            table.previousPage();
          }}
          disabled={!table.getCanPreviousPage()}
        >
          <ChevronLeft />
        </IconButton>
        {Array.from({length: endPageIndex - startPageIndex}, (_, i) => {
          const pageNum = startPageIndex + i;
          return (
            <IconButton
              key={pageNum}
              variant={pageIndex === pageNum ? 'secondary-filled' : 'secondary'}
              size="small"
              onClick={() => {
                table.setPageIndex(pageNum);
              }}
            >
              {pageNum + 1}
            </IconButton>
          );
        })}
        <IconButton
          variant="secondary"
          size="small"
          onClick={() => {
            table.nextPage();
          }}
          disabled={!table.getCanNextPage()}
        >
          <ChevronRight />
        </IconButton>
      </div>
      <div className="flex shrink-0 flex-row gap-2">
        <span>{t('Page')}</span>
        <strong>
          {table.getState().pagination.pageIndex + 1} /{' '}
          {table.getPageCount().toLocaleString()}
        </strong>
      </div>
    </div>
  );
}

export default TablePagination;
